import { ChevronDoubleLeftIcon } from "@heroicons/react/solid";
import BusinessDetails from "components/CorporateDashboard/BusinessDetails";
import CompaniesList from "components/CorporateDashboard/CompaniesList";
import DashboardAccessRequests from "components/CorporateDashboard/DashboardAccessRequests";
import MakeApplications from "components/CorporateDashboard/MakeApplications";
import MinuteBook from "components/CorporateDashboard/MinuteBook";
import TransactionsList from "components/CorporateDashboard/TransactionsList";
import DashboardWrapper from "components/lib/Global/DashboardWrapper";
import Hero from "components/lib/Global/Hero";
import FolderIcon from "components/lib/Shared/Icons/Folder";
import LadderIcon from "components/lib/Shared/Icons/Ladder";
import PointerIcon from "components/lib/Shared/Icons/Pointer";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import RoleSwitch from "components/lib/Shared/RoleSwitch";
import TransferYourBusinessDetails from "components/TransferYourBusiness/BusinessDetails";
import { Constants } from "config/constants";
import { Roles_Permissions_Constants } from "config/roles-permissions";
import { useSocket } from "context/SocketContext";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { getAccessRequests } from "state/slices/accessRequest";
import {
  resetActiveDashboardSlice,
  updateActiveDashboardState,
} from "state/slices/activeDashboard";
import { getOrganizationEmployeeAsync } from "state/slices/employee";
import { getCorporate, reset } from "state/slices/organizations";
import { setCookie } from "utils/cookie";
import MinuteBookSidebar from "./CorporateDashboard/MinuteBookSidebar";
import SidebarItem from "./CorporateDashboard/SidebarItem";
import { resetRoleswitch, updateOrgAdmin } from "state/slices/roleswitch";
import Billing from "components/CorporateDashboard/Billing";
import ComplianceDashboard from "components/Compliance/ComplianceDashboard";
import useComplianceAccess from "hooks/useComplianceAccess";

// Helper to build the general tabs for the sidebar
const buildGeneralTabs = (
  organization,
  viewRestrictedData,
  hasDocumentPermissions
) => {
  const baseTabs = [
    { id: "businessDetails", label: "Business Details" },
    { id: "registers", label: "Registers", disabled: true },
  ];
  if (
    ![Constants.APPLICANT_CAPACITIES.AUTHORIZED_PERSON].includes(
      organization?.partnershipType
    ) ||
    viewRestrictedData ||
    hasDocumentPermissions
  ) {
    baseTabs.push({
      id: "minuteBook",
      label: "Minute Book",
    });
  }
  return baseTabs;
};

// Helper to build the management tabs for the sidebar
const buildManagementTabs = (dashboardRoles, canAccessCompliance) => {
  const tabs = [];

  if (canAccessCompliance) {
    tabs.push({ id: "compliance", label: "Compliance Tracker" });
  }

  tabs.push({
    id: "companies",
    label: "Companies",
    disabled: !dashboardRoles?.some(
      (role) =>
        role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.REGISTRAR
    ),
  });

  tabs.push({
    id: "manageYourCompany",
    label: "Manage your company",
    disabled: true,
    comingSoon: true,
  });

  tabs.push({
    id: "contracts",
    label: "Contracts",
    disabled: true,
    comingSoon: true,
  });

  return tabs;
};

// Helper to build the audit tabs for the sidebar
const buildAuditTabs = (dashboardRoles, dashboardPermissions) => [
  { id: "billing", label: "Billing", disabled: false },
  {
    id: "accountSettings",
    label: "Account Settings",
    disabled:
      !dashboardRoles?.some(
        (role) =>
          role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.REGISTRAR
      ) &&
      !dashboardPermissions?.some(
        (permission) =>
          permission.name ===
          Roles_Permissions_Constants.DASHBOARD_PERMISSIONS.MANAGE_USERS
      ),
  },
  { id: "notifications", label: "Notifications", disabled: true },
  { id: "transactions", label: "Transactions" },
  { id: "auditTrail", label: "Audit Trail", disabled: true },
];

const CorporateDashboard = () => {
  // --- Variable Declarations ---
  const defaultTab = { id: "businessDetails", name: "Business Details" };
  const { corporateId } = useParams();

  const socket = useSocket();
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    organization,
    getOrganization,
    dashboardRoles,
    breadCrumbs,
    dashboardPermissions,
  } = useSelector((state) => state.organizationsSlice);
  const currentUser = useSelector((state) => state.userSlice);
  const [currentUserIsEmployee, setCurrentUserIsEmployee] = useState(false);
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState(() =>
    location.state?.selectedTabId
      ? {
        id: location.state?.selectedTabId,
        label: location.state?.selectedTabId,
      }
      : defaultTab
  );

  const { getEmployee } = useSelector((state) => state.employeeSlice);

  const { isTransferYourBusiness } = useSelector(
    (state) => state.transferYourBusinessSlice
  );
  const { canAccessCompliance } = useComplianceAccess();

  useEffect(() => {
    if (getEmployee?.items?.length > 0) {
      const employeeFound = getEmployee.items.find(
        (employee) => employee.user._id === currentUser._id
      );
      if (employeeFound) {
        setCurrentUserIsEmployee(true);
      }
    }
  }, [getEmployee]);

  // Permissions and roles
  const viewRestrictedData = dashboardPermissions?.some(
    (permission) =>
      permission.name ===
      Roles_Permissions_Constants.DASHBOARD_PERMISSIONS.VIEW_RESTRICTED_DATA
  );
  const documentPermissionValues = Object.values(
    Roles_Permissions_Constants.DOCUMENT_PERMISSIONS
  );
  const hasDocumentPermissions = dashboardPermissions?.some((docPermission) =>
    documentPermissionValues.includes(docPermission.name)
  );

  // Memoized tab arrays for sidebar
  const generalTabs = useMemo(
    () =>
      buildGeneralTabs(
        organization,
        viewRestrictedData,
        hasDocumentPermissions
      ),
    [viewRestrictedData, organization?.partnershipType, hasDocumentPermissions]
  );
  const managementTabs = useMemo(
    () => buildManagementTabs(dashboardRoles, canAccessCompliance),
    [dashboardRoles, canAccessCompliance]
  );
  const auditTabs = useMemo(
    () => buildAuditTabs(dashboardRoles, dashboardPermissions),
    [dashboardRoles, dashboardPermissions]
  );

  useEffect(() => {
    if (!canAccessCompliance && selectedTab?.id === "compliance") {
      setSelectedTab(defaultTab);
    }
  }, [canAccessCompliance, selectedTab?.id]);

  // Pages for breadcrumbs
  const pages =
    breadCrumbs.length > 0
      ? [
        { name: "My Companies", href: "/companies", current: false },
        ...breadCrumbs,
      ]
      : [
        { name: "My Companies", href: "/companies", current: false },
        organization?.names?.companyName
          ? {
            name: organization?.names?.companyName,
            href: "#",
            current: true,
          }
          : null,
      ];

  // --- Function Declarations ---
  useEffect(() => {
    const selectedTabId = location.search.split("?")[1];
    if (selectedTabId) {
      setSelectedTab({
        id: selectedTabId,
        label: selectedTabId,
      });

      history.replace({
        pathname: location.pathname,
        search: "",
        state: {
          selectedTabId,
        },
      });
    }
  }, [history, location.search]);

  useEffect(() => {
    let getCorporatePromise = {};
    if (corporateId && currentUser?._id) {
      getCorporatePromise = dispatch(
        getCorporate({ corporateId, isDashboard: true })
      );
      dispatch(getAccessRequests({ query: { createdBy: currentUser?._id } }));
    }
    return () => {
      if (typeof getCorporatePromise?.abort === "function") {
        getCorporatePromise.abort();
      }
      dispatch(reset());
    };
  }, [corporateId, currentUser?._id]);

  useEffect(() => {
    socket.emit("join-corporate-dashboard", corporateId);
    socket.on("refresh-primarySource-corporate", (corporateId) => {
      dispatch(getCorporate({ corporateId, isDashboard: true }));
    });
    return () => {
      socket.emit("leave-corporate-dashboard", corporateId);
    };
  }, [corporateId]);

  useEffect(() => {
    dispatch(resetRoleswitch());

    if (organization?.status === "Active") {
      if (organization?.isOrgAdmin) {
        dispatch(
          updateOrgAdmin({
            orgAdmin: organization._id,
          })
        );
      }

      dispatch(
        updateActiveDashboardState({
          dashboardUrl: `/corporates/${organization._id}/dashboard`,
        })
      );
    }
    if (organization?._id) {
      dispatch(
        getOrganizationEmployeeAsync({ organizationId: organization._id })
      );
    }
  }, [organization]);

  const handleTabClick = (item) => {
    if (item?.disabled) return;
    setSelectedTab(item);
  };

  const handleMakeApplication = () => {
    setSelectedTab({
      id: "makeApplications",
      name: "Make Applications",
    });
  };

  const goToBusinessDetails = () =>
    setSelectedTab({ id: "businessDetails", name: "Business Details" });

  const handleExitDashboard = () => {
    setCookie("role", Constants.COOKIE_ROLES.APPLICANT);
    dispatch(resetActiveDashboardSlice());
    history.push("/companies");
  };

  // Sidebar rendering logic
  const renderSidebar = () => {
    if (selectedTab?.id === "minuteBook") {
      return (
        <div className="col-span-5 md:col-span-1 pt-8">
          <div
            className="flex items-center my-2 text-gray-600"
            onClick={() => setSelectedTab(defaultTab)}
          >
            <ChevronDoubleLeftIcon className="w-5 h-5 mr-2" />
            <span className="cursor-pointer">Back to main sidebar</span>
          </div>
          <MinuteBookSidebar
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            goToBusinessDetails={goToBusinessDetails}
          />
        </div>
      );
    }
    return (
      <div className="col-span-4 md:col-span-1 pt-8">
        <div>
          <h4 className="mb-2 text-sflPrimary font-medium flex items-center">
            <FolderIcon className="w-5 h-5" aria-hidden="true" />
            <span className="pl-2">General</span>
          </h4>
          <ul className="ml-10">
            {generalTabs.map((item) => (
              <SidebarItem
                key={item.label}
                item={item}
                selectedTab={selectedTab}
                handleTabClick={handleTabClick}
              />
            ))}
          </ul>
        </div>
        <div className="border-t pt-2">
          <h4 className="mb-2 text-sflPrimary font-medium flex items-center">
            <LadderIcon className="w-5 h-5" aria-hidden="true" />
            <span className="pl-2">Management</span>
          </h4>
          <ul className="ml-10">
            {managementTabs.map((item) => (
              <SidebarItem
                key={item.label}
                item={item}
                selectedTab={selectedTab}
                handleTabClick={handleTabClick}
              />
            ))}
          </ul>
        </div>
        <div className="border-t pt-2">
          <h4 className="mb-2 text-sflPrimary font-medium flex items-center">
            <PointerIcon className="w-5 h-5" aria-hidden="true" />
            <span className="pl-2">Audit</span>
          </h4>
          <ul className="ml-10">
            {auditTabs.map((item) => (
              <SidebarItem
                key={item.label}
                item={item}
                selectedTab={selectedTab}
                handleTabClick={handleTabClick}
              />
            ))}
          </ul>
        </div>
        <div className="border-t pt-2">
          <button
            className="bg-sflPrimary w-full text-white font-bold py-2 px-4 rounded mb-4"
            onClick={handleMakeApplication}
          >
            Make Applications
          </button>
        </div>
      </div>
    );
  };

  // --- Render Logic ---
  return (
    <DashboardWrapper
      pages={pages}
      showBreadCrumbs
      exitDashboardText="Exit to personal Dashboard"
      handleExitDashboard={handleExitDashboard}
    >
      {currentUser && currentUser?.isOnboarded && (
        <div className="grid gap-10 py-10 mx-auto">
          <div className="flex justify-between">
            <Hero
              heading={
                organization?.names?.companyName ??
                <SpinnerIcon className="text-gray-400" />
              }
            />
            {organization?._id && organization.status === "Active" && (
              <div>
                <RoleSwitch
                  organizationName={organization?.names?.companyName}
                  organization={organization}
                  dashboardPermissions={dashboardPermissions}
                  dashboardRoles={dashboardRoles}
                  breadCrumbs={pages}
                  dashboardUrl={history.location.pathname}
                />
              </div>
            )}
          </div>
          <div className="grid grid-cols-4 border-t border-gray">
            <div className="col-span-4 md:col-span-1 pt-2">
              {renderSidebar()}
            </div>
            <div className="col-span-4 md:border-l md:col-span-3 md:pl-11 pt-14 border-gray">
              {getOrganization.status === "loading" || !organization?._id ? (
                <div className="flex justify-center mx-auto text-white">
                  <SpinnerIcon className="text-gray-400" />
                </div>
              ) : (
                <>
                  {selectedTab?.id === "transferYourBusiness" && (
                    <TransferYourBusinessDetails partialData={organization} />
                  )}
                  {selectedTab?.id === "businessDetails" && <BusinessDetails />}
                  {selectedTab?.id === "minuteBook" && <MinuteBook />}
                  {selectedTab?.id === "compliance" && (
                    <ComplianceDashboard corporateId={corporateId} />
                  )}
                  {selectedTab?.id === "transactions" && <TransactionsList />}
                  {selectedTab?.id === "companies" && (
                    <CompaniesList goToBusinessDetails={goToBusinessDetails} />
                  )}
                  {selectedTab?.id === "accountSettings" && (
                    <DashboardAccessRequests
                      currentUserIsEmployee={currentUserIsEmployee}
                    />
                  )}
                  {selectedTab.id === "billing" && <Billing />}
                  {selectedTab.id === "makeApplications" && (
                    <MakeApplications />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardWrapper>
  );
};

export default CorporateDashboard;
